import { Routes, Route, useLocation } from "react-router-dom";
import LoginForm from "../pages/Auth/LoginPage"; // Adjust the import path to where your LoginForm is located
import ProtectedRoute from "./ProtectedRoute";
import Dashboard from "pages/Dashboard";
import Layout from "components/layout/Layout";
import UserListing from "pages/Users";
import VendorListing from "pages/Purchases/vendors";
import TransactionListing from "pages/Accounting/Transactions";
import CustomerListing from "pages/Sales/Customers";
import ProductListing from "pages/Inventory/Products";
import ServiceListingPage from "pages/Inventory/Services";
import NotFound from "pages/NotFound";
import { ServiceCategoryListing } from "pages/Inventory/ServiceCategory";
import LocationListingPage from "pages/Inventory/Location";
import ProductCategoryListingPage from "pages/Inventory/ProductCategory";
import BrandListingPage from "pages/Inventory/Brand";
import SettingsPage from "pages/Settings";
import TaxListingPage from "pages/Accounting/Tax";
import CurrencyListingPage from "pages/Accounting/Currency";
import PurchaseOrderListing from "pages/Purchases/PurchaseOrders";
import ProductView from "pages/Inventory/Products/ProductView";
import PurchaseOrderDetails from "pages/Purchases/PurchaseOrders/PurchaseOrderDetails";
import { ExpenseCategoryListingPage } from "pages/Accounting/ExpenseCategories";
import ExpenseListingPage from "pages/Accounting/Expenses";
import BanksListingPage from "pages/Accounting/Banks";
import InvoiceListing from "pages/Sales/Invoice";
import RolesSettingsPage from "pages/Settings/Roles";
import EditRole from "pages/Settings/Roles/EditRole";
import InventoryTransactionListing from "pages/Inventory/Inventory";
import VendorDetailsPage from "pages/Purchases/vendors/VendorDetailsPage";
import ProductVariantsSettingsPage from "pages/Settings/Variants";
import ProductModelSettingsPage from "pages/Settings/ProductModel";
import CompanyDetails from "pages/Settings/CompanyDetails";
import BackupComponent from "pages/Settings/BackupComponent";
import ReportsPage from "pages/Reports";
import StockListing from "pages/Inventory/Stock";
import InvoiceDetails from "pages/Sales/Invoice/InvoiceDetails";
import ReparationList from "pages/Sales/Reparations";
import ReparationCaseDetails from "pages/Sales/Reparations/components/ReparationCaseDetails";
import {
  useSettingsServiceGetCompanyDetails,
  useSettingsServiceGetCompanyDetailsKey,
} from "api/queries";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import cookies from "utils/cookies";
import ProductBarcodes from "pages/Inventory/Barcode";
import ChartOfAccountsV2 from "pages/Accounting/Accounts/ChartOfAccountv2";
import PaymentMethodListing from "pages/Settings/PaymentMethods";
import SalesSummaryReport from "pages/Reports/SalesSummaryReport";
import SalesByProductReport from "pages/Reports/SalesByProductReport";
import SalesByCustomerReport from "pages/Reports/SalesByCustomerReport";
import SalesByEmployeeReport from "pages/Reports/SalesByEmployeeReport";
import InvoiceAgingReport from "pages/Reports/InvoiceAgingReport";
import WarehouseStockReport from "pages/Reports/WarehouseStockReport";
import WarehouseTransferReport from "pages/Reports/WarehouseTransferReport";
import LowStockReport from "pages/Reports/LowStockReport";
import OutOfStockReport from "pages/Reports/OutOfStockReport";
import StockValuationReport from "pages/Reports/StockValuationReport";
import StockMovementReport from "pages/Reports/StockMovementReport";
import PurchaseOrderSummary from "pages/Reports/components/PurchaseReports/PurchaseOrderSummary";
import PendingDeliveryPurchaseOrders from "pages/Reports/components/PurchaseReports/PendingDeliveryPurchaseOrders";
import PurchaseSummary from "pages/Reports/components/PurchaseReports/PurchaseSummary";
import ProfitAndLossReport from "pages/Reports/components/FinancialReports/ProfitAndLossReport";
import DailyMonthlyTransactionsReport from "pages/Reports/components/FinancialReports/DailyMonthlyTransactionsReport";
import ExpenseReport from "pages/Reports/components/FinancialReports/ExpenseReport";
import { setCompany } from "utils/redux/slices";
import SystemLog from "pages/Reports/components/UserReports/SystemLog";
import SalesByUser from "pages/Reports/components/UserReports/SalesByUser";
import CustomerOverview from "pages/Reports/components/CustomerReports/CustomerOverview";
import BalanceSheetReport from "pages/Reports/components/FinancialReports/BalanceSheetReport";
import ReparationCaseSummary from "pages/Reports/components/sales/ReparationCaseSummary";
import SalesReport from "pages/Reports/components/sales/SalesReport";
import ProfitAndLossReportV2 from "pages/Reports/components/FinancialReports/ProfitAndLossReportV2";

const AppRoutes = () => {
  const location = useLocation();

  const auth = useSelector((state: any) => state.auth);
  const company = useSelector((state: any) => state.company);
  const dispatch = useDispatch();

  const { data, isLoading }: any = useSettingsServiceGetCompanyDetails(
    [useSettingsServiceGetCompanyDetailsKey],
    {
      retry: false,
      refetchInterval: false,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    }
  );

  const handleLocalData = () => {
    const company: string = cookies.get("company");

    if (!company && data && !isLoading) {
      dispatch(
        setCompany({
          currency: data.baseCurrency,
          currencyCode: data.currencyCode,
          currencySymbol: data.currencySymbol,
          companyName: data.companyName,
          companyAddress: data.companyAddress,
          companyPhone: data.companyPhone,
          companyEmail: data.companyEmail,
          companyLogo: "", // Add if present in your cookies
          companyWebsite: data.companyWebsite,
          companyVat: data.companyVat || "", // Add if present in your cookies
        })
      );
      cookies.set("company", data);
    }

    if (company) {
      try {
        // Parse company data from JSON string
        const companyData = JSON.parse(company);

        dispatch(
          setCompany({
            currency: companyData.baseCurrency,
            currencyCode: companyData.currencyCode,
            currencySymbol: companyData.currencySymbol,
            companyName: companyData.companyName,
            companyAddress: companyData.companyAddress,
            companyPhone: companyData.companyPhone,
            companyEmail: companyData.companyEmail,
            companyLogo: "", // Add if present in your cookies
            companyWebsite: companyData.companyWebsite,
            companyVat: companyData.companyVat || "", // Add if present in your cookies
          })
        );
      } catch (error) {
        console.error("Failed to parse company data:", error);
      }
    }
  };

  useEffect(() => {
    handleLocalData();
  }, [data]);

  return (
    <Routes location={location} key={location.key}>
      <Route key="route-login" path="/login" element={<LoginForm />} />
      <Route key="route-root" path="/" element={<ProtectedRoute />}>
        <Route
          key="route-layout"
          element={<Layout />}
          children={[
            <Route key="route-dashboard" path="/" element={<Dashboard />} />,
            <Route
              key="route-chart-of-accounts-routes"
              path="/accounting"
              children={[
                <Route
                  key="route-chart-of-accounts"
                  path="/accounting/chart-of-accounts"
                  element={<ChartOfAccountsV2 />}
                />,
                <Route
                  key="route-transaction-listing"
                  path="/accounting/transaction-listing"
                  element={<TransactionListing />}
                />,
                <Route
                  key="route-bank-accounts"
                  path="/accounting/bank-accounts"
                  element={<BanksListingPage />}
                />,
                <Route
                  key="route-expense-categories-listing"
                  path="expense-categories"
                  element={<ExpenseCategoryListingPage />}
                />,
                <Route
                  key="route-expenses-listing"
                  path="expenses"
                  element={<ExpenseListingPage />}
                />,
                <Route
                  key="route-tax-listing"
                  path="/accounting/tax"
                  element={<TaxListingPage />}
                />,

                <Route
                  key="route-currencies"
                  path="/accounting/currencies"
                  element={<CurrencyListingPage />}
                />,
              ]}
            />,
            <Route
              key="route-users"
              path="/users"
              children={[
                <Route path="" key={"user-list"} element={<UserListing />} />,
              ]}
            />,
            <Route
              key="route-customers"
              path="/sales"
              children={[
                <Route
                  path="customers"
                  key={"customer-list"}
                  element={<CustomerListing />}
                />,
                <Route
                  path="reparations"
                  key={"reparation-list"}
                  element={<ReparationList />}
                />,
                <Route
                  path="reparations/:id"
                  key="reparation-details"
                  element={<ReparationCaseDetails />}
                />,
                <Route
                  path="invoices"
                  key={"invoice-list"}
                  children={[
                    <Route
                      key="route-invoice-list"
                      path=""
                      element={<InvoiceListing />}
                    />,
                    <Route
                      key="route-invoice-details"
                      path=":id"
                      element={<InvoiceDetails />}
                    />,
                  ]}
                />,
              ]}
            />,
            <Route
              key="route-purchases"
              path="/purchases"
              children={[
                <Route
                  path="/purchases/vendors"
                  key={"vendor-list"}
                  element={<VendorListing />}
                />,
                <Route
                  key={"vendor-details"}
                  path="/purchases/vendors/:id"
                  element={<VendorDetailsPage />}
                />,
                <Route
                  path="/purchases/purchase-orders"
                  key={"purchases-order-list"}
                  children={[
                    <Route
                      key="route-purchase-orders"
                      path=""
                      element={<PurchaseOrderListing />}
                    />,
                    <Route
                      key="route-purchase-orders-create"
                      path=":id"
                      element={<PurchaseOrderDetails />}
                    />,
                  ]}
                />,
              ]}
            />,
            <Route
              key="route-inventory"
              path="/inventory"
              children={[
                <Route
                  key="route-transactions"
                  path="/inventory/transactions"
                  element={<InventoryTransactionListing />}
                />,
                <Route
                  key="route-products"
                  path="/inventory/products"
                  element={<ProductListing />}
                />,
                <Route
                  key="route-product-view"
                  path="/inventory/products/:id"
                  element={<ProductView />}
                />,
                <Route
                  key="route-product-category"
                  path="/inventory/product-category"
                  element={<ProductCategoryListingPage />}
                />,
                <Route
                  key="route-brands"
                  path="/inventory/brands"
                  element={<BrandListingPage />}
                />,
                <Route
                  key="route-services"
                  path="/inventory/services"
                  element={<ServiceListingPage />}
                />,
                <Route
                  key="route-service-category"
                  path="/inventory/service-category"
                  element={<ServiceCategoryListing />}
                />,
                <Route
                  key="route-location"
                  path="/inventory/location"
                  element={<LocationListingPage />}
                />,
                <Route
                  key={"route-stock"}
                  path="/inventory/stock"
                  element={<StockListing />}
                />,
                <Route
                  key="product-barcode"
                  path="/inventory/barcode"
                  element={<ProductBarcodes />}
                />,
              ]}
            />,
            <Route
              key="route-settings"
              path="/settings"
              element={<SettingsPage />}
              children={[
                <Route
                  key="route-company-details"
                  path="/settings/company-details"
                  element={<CompanyDetails />}
                />,
                <Route
                  key="route-settings-roles"
                  path="/settings/roles"
                  children={[
                    <Route
                      key="route-settings-roles-routes"
                      path="/settings/roles"
                      element={<RolesSettingsPage />}
                    />,
                    <Route
                      key="route-roles-edit"
                      path="/settings/roles/:id"
                      element={<EditRole />}
                    />,
                  ]}
                />,
                <Route
                  key={"route-settings-product-variants"}
                  path="/settings/product-variants"
                  element={<ProductVariantsSettingsPage />}
                />,
                <Route
                  key={"route-settings-product-models"}
                  path="/settings/product-models"
                  element={<ProductModelSettingsPage />}
                />,
                <Route
                  key={"route-settings-backup"}
                  path="/settings/backup"
                  element={<BackupComponent />}
                />,
                <Route
                  key={"route-settings-payment-methods"}
                  path="/settings/payment-methods"
                  element={<PaymentMethodListing />}
                />,
              ]}
            />,
            <Route
              key="route-reports"
              path="/reports"
              children={[
                <Route
                  key="route-reports-main"
                  path=""
                  element={<ReportsPage />}
                />,
                <Route
                  key="route-warehouse-stock"
                  path="warehouse-stock"
                  element={<WarehouseStockReport />}
                />,
                <Route
                  key="route-warehouse-transfers"
                  path="warehouse-transfers"
                  element={<WarehouseTransferReport />}
                />,
                <Route
                  key="route-low-stock"
                  path="low-stock"
                  element={<LowStockReport />}
                />,
                <Route
                  key="route-out-of-stock"
                  path="out-of-stock"
                  element={<OutOfStockReport />}
                />,
                <Route
                  key="route-stock-valuation"
                  path="stock-valuation"
                  element={<StockValuationReport />}
                />,
                <Route
                  key="route-stock-movement"
                  path="stock-movement"
                  element={<StockMovementReport />}
                />,
                <Route
                  key="route-sales-report-v2"
                  path="sales-report"
                  element={<SalesReport />}
                />,
                <Route
                  key="route-sales-summary"
                  path="sales-summary"
                  element={<SalesSummaryReport />}
                />,
                <Route
                  key="route-sales-by-product"
                  path="sales-by-product"
                  element={<SalesByProductReport />}
                />,
                <Route
                  key="route-sales-by-customer"
                  path="sales-by-customer"
                  element={<SalesByCustomerReport />}
                />,
                <Route
                  key="route-sales-by-employee"
                  path="sales-by-employee"
                  element={<SalesByEmployeeReport />}
                />,
                <Route
                  key="route-invoice-aging"
                  path="invoice-aging"
                  element={<InvoiceAgingReport />}
                />,
                <Route
                  key="route-purchase-order-summary"
                  path="purchases/order-summary"
                  element={<PurchaseOrderSummary />}
                />,
                <Route
                  key="route-pending-delivery-purchase-orders"
                  path="purchases/pending-delivery"
                  element={<PendingDeliveryPurchaseOrders />}
                />,
                <Route
                  key="route-purchase-summary"
                  path="purchases/summary"
                  element={<PurchaseSummary />}
                />,
                <Route
                  key="route-profit-and-loss-report"
                  path="financial/profit-and-loss"
                  element={<ProfitAndLossReportV2 />}
                />,
                <Route
                  key="route-daily-monthly-transactions-report"
                  path="financial/daily-monthly-transactions"
                  element={<DailyMonthlyTransactionsReport />}
                />,
                <Route
                  key="route-expense-report"
                  path="financial/expense-report"
                  element={<ExpenseReport />}
                />,
                <Route
                  key="route-user-reports-sales-by-user"
                  path="user-reports/sales-by-user"
                  element={<SalesByUser />}
                />,
                <Route
                  key="route-user-reports-system-log"
                  path="user-reports/system-log"
                  element={<SystemLog />}
                />,
                <Route
                  key="route-customer-overview"
                  path="customer-overview"
                  element={<CustomerOverview />}
                />, // Customer Overview Report Route
                <Route
                  key="route-balance-sheet-report"
                  path="financial/balance-sheet"
                  element={<BalanceSheetReport />}
                />,
                <Route
                  key="route-reparation-case-summary"
                  path="reparation-case-summary"
                  element={<ReparationCaseSummary />}
                />,
              ]}
            />,
          ]}
        />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
